import React, { useState, useEffect, useMemo } from "react";
import { PropTypes } from "prop-types";
import { useLocation, useNavigate } from "react-router-dom";
import reactWrapper from "@harbor/elements/utils/react/wrapper";
import AppHOC from "../../generics/AppHOC";
import SiteNetworkHierarchyWidget from "../../generics/SiteNetworkHierarchy";
import BetaTag from "../BetaTag";
import {
  isAllSitesPage,
  updateCookie,
  isSecurityPage,
  isTrafficLogsPage,
  onTabClick,
  isBandForecastingTabSelected,
  isAnomalyPage
} from "../../utils/common";
import { getMainTabs, getADAccess } from "../../utils/license";
import { getCookie } from "../../serviceWorker";
import { useI18n } from "../../i18n/useI18n";
import css from "../commonMagneticStyle.less";
import { adUrls } from "../../utils/enums";

const [HbrTabGroup, HbrTab] = reactWrapper(["hbr-tab-group", "hbr-tab"]);

const TabNavigation = props => {
  const { tCommon } = useI18n('common');
  const [tabs, setTabs] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const vManageURI = getCookie("cl-vManageURI");
  const isUIconvergence = getCookie("cl-analytics");
  const adAccess = useMemo(() => getADAccess(), []);

  const activeTabLabel = useMemo(() => {
    for (const item of tabs) {
      if (item.path) {
        if (location.pathname === item.path || location.pathname.includes(item.path))
          return item.label;
      } else if (location.pathname.includes(item.url)) return item.label;
      else if (location.pathname.includes(item.url.replace("/", ""))) return item.label;//log tab to show active
    }
  }, [location.pathname, tabs]);

  const tabGroupKey = useMemo(
    () => tabs.map(({ label, path, url }) => `${label}:${path || url || ""}`).join("|"),
    [tabs]
  );

  // ✅ Helper function to determine if site selector should be shown
  const shouldShowSiteSelector = () => {
    // Don't show for all sites, security, traffic logs, or bandwidth forecasting pages
    if (isAllSitesPage() || isSecurityPage() || isTrafficLogsPage() || isBandForecastingTabSelected()) {
      return false;
    }

    // ✅ Check onboarding status from props first, then localStorage
    const onboardingStatus = props.globalFilter?.onboardingStatus ||
      localStorage.getItem("onboardingStatus");

    // Determine visibility based on onboarding status
    if (onboardingStatus === "ACTIVE") {
      return true;
    } else if (isAnomalyPage()) {
      // Always show on anomaly page
      return true;
    } else if (onboardingStatus === "ERROR") {
      // If there's an error, don't show the selector
      return false;
    } else {
      // Default to showing the selector
      return true;
    }
  };

  const removePPRCookies = () => {
    localStorage.removeItem('bandForecastingTab');
    localStorage.removeItem('onboardingStatus');
  }

  useEffect(() => {
    const responseFromAPI = getMainTabs(props.globalFilter.features);
    setTabs(responseFromAPI)
  }, [
    props.globalFilter.selectedSite,
    props.globalFilter.menuState,
    props.globalFilter.features
  ]);

  const onTabsChange = tab => {
    // Update current path in localStorage
    localStorage.setItem("currentPath", tab.label);

    // UI Convergence handling
    if (vManageURI && isUIconvergence) {
      if (tab.path) {
        updateCookie("currentPath", tab.path.substring(1));
        removePPRCookies();
        navigate(tab.path);
      } else {
        removePPRCookies();
        onTabClick(tab, isUIconvergence);
      }
      return;
    }

    // Standard vAnalytics handling
    //const isPNPage = isPredictiveNetworksPage();
    const isTargetPNPage = tab.path.endsWith(adUrls.pn);

    // Clear AD-related storage when navigating to PN with AD access
    if (isTargetPNPage && adAccess.available && adAccess.enabled) {
      localStorage.removeItem("selectedADTab");
      localStorage.removeItem("selectedUsageADTab");
      navigate(tab.path + adUrls.ad);
      return;
    }

    // Clear PN-specific storage when leaving PN pages
    removePPRCookies();

    navigate(tab.path);
  };

  return (
    <div className={css["tab-navigation"]}>
      <div className="tabs-component">
        <div className="analytics-top-widget flex-items">
          <div className="time-filter flex-items">
            <div className="app-label hbr-type-h1" role="heading" aria-level={'1'}>
              {(vManageURI && isUIconvergence)
                ? tCommon('monitorLabel')
                : tCommon('analyticsLabel')}
            </div>
            {/* ✅ Conditionally render based on all conditions */}
            {shouldShowSiteSelector() ? (
              <SiteNetworkHierarchyWidget {...props} />
            ) : (
              <div data-testid="no-sites-dropdown"></div>
            )}
          </div>
        </div>
        <div className="tabs">
          {tabs.length > 0 && (
            <HbrTabGroup key={tabGroupKey} primary>
              {tabs.map(item => (
                <HbrTab
                  key={item.label}
                  data-testid={`tab-${item.label}`}
                  slot="nav"
                  sentiment="success"
                  panel={item.label}
                  onHbr-click={() => onTabsChange(item)}
                  active={activeTabLabel === item.label}
                >
                  {item.label}
                  <div data-testid={`tab-${item.label}-suffix`} slot="suffix"></div>
                  {item.beta === true && (<div slot="suffix"><BetaTag className={"tab-class"} /></div>)}
                </HbrTab>
              ))}
            </HbrTabGroup>
          )}
        </div>
      </div>
    </div >
  );
};

TabNavigation.propTypes = {
  globalFilter: PropTypes.object.isRequired
};

export default AppHOC(TabNavigation);
